import axios from "axios";
import { queueRequest, listQueued, deleteQueued, countQueued } from "./syncQueue";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const isMutation = (method) => ["post", "put", "patch", "delete"].includes((method || "").toLowerCase());
const isOfflineError = (err) => !err.response && (err.code === "ERR_NETWORK" || err.message === "Network Error" || (typeof navigator !== "undefined" && !navigator.onLine));

// On mutation failure due to offline → queue it
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const cfg = err.config || {};
    if (cfg.__skipQueue || cfg.__isReplay) throw err;
    if (isMutation(cfg.method) && isOfflineError(err)) {
      const queued = await queueRequest({
        method: cfg.method,
        url: cfg.url,
        baseURL: cfg.baseURL,
        data: cfg.data,
        headers: cfg.headers ? { "Content-Type": cfg.headers["Content-Type"] || "application/json" } : undefined,
        ts: new Date().toISOString(),
      });
      if (queued) {
        toast.info("Saved offline — will sync when you reconnect");
        // Return a synthetic 202 response so callers don't crash
        return Promise.resolve({ status: 202, data: { queued: true, offline: true }, headers: {}, config: cfg });
      }
    }
    throw err;
  }
);

let syncing = false;
export async function replayQueue() {
  if (syncing || typeof navigator === "undefined" || !navigator.onLine) return { synced: 0 };
  syncing = true;
  let synced = 0, failed = 0;
  try {
    const items = await listQueued();
    for (const it of items) {
      try {
        let data = it.data;
        if (typeof data === "string") {
          try { data = JSON.parse(data); } catch { /* keep as string */ }
        }
        await api.request({
          method: it.method,
          url: it.url,
          baseURL: it.baseURL,
          data,
          headers: it.headers,
          __isReplay: true,
        });
        await deleteQueued(it.id);
        synced++;
      } catch {
        failed++;
      }
    }
    if (synced > 0) toast.success(`${synced} offline change${synced > 1 ? "s" : ""} synced`);
  } finally {
    syncing = false;
  }
  return { synced, failed };
}

// Auto-replay when we come back online
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { replayQueue(); });
}

export { countQueued };
export default api;
